import { AxiosResponse } from 'axios';
import _ from 'lodash';
import config from '../config';
import { Products, Routes } from '../enums';
import Logger from '../logger';
import { API_V1 } from './APIs/APIv1';

export interface Product {
  product: Products;
  quantity: number;
}

export enum Period {
  MONTH = 'month',
  QUARTER = 'quarter',
  YEAR = 'year',
  NONE = 'none',
}

export interface SinglePrice {
  monthly?: number;
  quarterly?: number;
  yearly?: number;
}

export type PriceResponse = {
  [key in Products]?: SinglePrice;
} & { currency: string };

export interface StripeCheckoutSession {
  amount_total?: number;
  currency?: string;
  payment_intent?: string;
  customer?: string;
  customer_email?: string;
}

export interface CreateCheckoutRequestBody {
  period: Period;
  products: Product[];
  currency?: string;
  successUrl: string;
  cancelUrl: string;
  clientReferenceId?: any;
  /** Stripe promotion code from an offer link; the API puts it straight onto the checkout. */
  promotionCode?: string;
}

/**
 * Promotion code from an offer link (`/upgrade?promotionCode=…`), remembered for
 * the session so it survives the way through the plan picker. Anything that does
 * not look like a Stripe code is ignored rather than sent.
 */
export function offerPromotionCode(search: string = window.location.search): string | undefined {
  const KEY = 'saia_promotion_code';
  const valid = (v: string | null) => (v && /^[A-Za-z0-9_-]{3,64}$/.test(v) ? v : undefined);
  const fromUrl = valid(new URLSearchParams(search).get('promotionCode'));
  try {
    if (fromUrl) sessionStorage.setItem(KEY, fromUrl);
    return fromUrl ?? valid(sessionStorage.getItem(KEY));
  } catch {
    return fromUrl;
  }
}

export const createCheckoutSession = async (
  period: Period,
  products: Product[],
  _licenseId?: string,
  clientReferenceId?: any,
  baseURL?: string,
  currency?: string,
  promotionCode?: string,
): Promise<AxiosResponse<any, any>['data']> => {
  try {
    let successUrl = '/account';
    products.forEach((item, index) => {
      if (index === 0) {
        successUrl += `?product=${item.product}`;
      }

      if (index === 1) {
        successUrl += `&more=${item.product}`;
      }
    });
    const body: CreateCheckoutRequestBody = {
      period,
      products,
      currency,
      successUrl: (baseURL ? baseURL : config.dashboardUrl) + successUrl,
      cancelUrl: (baseURL ? baseURL : config.dashboardUrl) + Routes.UPGRADE,
    };
    if (clientReferenceId) {
      body.clientReferenceId = clientReferenceId;
    }
    if (promotionCode) {
      body.promotionCode = promotionCode;
    }
    const response = await API_V1.post('/payments/checkout-session', body);
    return { sessionId: response.data.sessionId };
  } catch (e) {
    Logger.trace('Error while creating checkout session');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const createCustomerPortalSession = async () => {
  try {
    const body = {
      // Zurück zur Lizenzübersicht, nicht zur Kaufseite
      returnUrl: config.dashboardUrl + '/account/licenses',
    };

    const response = await API_V1.post('/payments/customer-portal-session', body);
    return { sessionId: response.data.sessionId, sessionUrl: response.data.sessionUrl };
  } catch (e) {
    Logger.trace('Error while creating checkout session');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getPrices = async () => {
  try {
    const response = await API_V1.get('/payments/price');
    return response.data;
  } catch (e) {
    Logger.trace('Error while getting prices');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getCheckoutSession = async (sessionId: string): Promise<{ data?: StripeCheckoutSession; error?: any }> => {
  try {
    const response = await API_V1.get('/payments/checkout-session-details', {
      params: { session_id: sessionId },
    });
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while fetching checkout session');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};
